
import Vue from "vue";
import { Component } from "vue-property-decorator";
import BaseAuthenticatedView from "@/views/BaseAuthenticatedView.vue";
import { Emit } from "@/api/apiHelper";
import EmailAndPasswordForm from "./components/EmailAndPasswordForm.vue";
import OTPForm from "./components/OTPForm.vue";
import isString from "lodash/isString";
import { startMessenger } from "@/services/tawkToMessengerService";

@Component({
  components: { EmailAndPasswordForm, OTPForm },
})
export default class Login extends BaseAuthenticatedView {
  // 🗻 The possible login steps
  private VISIBLE_STEP = {
    EMAIL_AND_PASSWORD: 1,
    TWO_FACTOR_AUTENTICATION: 2,
  };

  // 📦 The currently visible step for the login flow
  private currentlyVisibleStep: number = this.VISIBLE_STEP.EMAIL_AND_PASSWORD;

  // 📦 Email that the user enters
  private email: string | null = null;

  // 📦 Passowrd that the user enters
  private password: string | null = null;

  // 📦 OTP code for the 2FA
  private otpCode: string | null = null;

  // 🔃 Flag responsible for showing loading indicator
  private isLoading: boolean = false;

  // 🚩 Flag for whether the provided email is valid
  private get isProvidedInformationValid(): boolean {
    return this.email != null && this.email !== "" && this.password !== null;
  }

  protected async mounted() {
    // Check if any query parameters were passed and if so - prefill the local state
    let query = this.$route.query;
    if (
      query.name &&
      isString(query.name) &&
      query.email &&
      isString(query.email) &&
      query.authToken &&
      isString(query.authToken) &&
      query.createdOn
    ) {
      this.showInformationSnackbar("Navigating to the app...");
      this.prefillLocalStateFromQuery(
        query.name as string,
        query.email as string,
        query.authToken as string,
        Number(query.createdOn)
      );
    } else {
      await super.mounted();
    }
  }

  /**
   * 🚀 Check for whether the 2FA is enabled for the account
   */
  private async checkFor2FA() {
    if (!this.isProvidedInformationValid) {
      return;
    }

    try {
      this.isLoading = true;
      let checkResponse = await Vue.prototype.$api.post(
        "/api/v1/login/checkotp",
        {
          username: this.email,
          password: this.password,
        }
      );

      this.$store.commit("SET_2FA_ENABLED", checkResponse.data.enabled);

      if (checkResponse.data.enabled) {
        this.isLoading = false;
        this.currentlyVisibleStep = this.VISIBLE_STEP.TWO_FACTOR_AUTENTICATION;
        return;
      }
      this.login();
    } catch (error) {
      this.isLoading = false;
    }
  }

  /**
   * 🚀 Logs in the current user into the system
   */
  private async login() {
    if (!this.isProvidedInformationValid) {
      return;
    }

    try {
      this.isLoading = true;
      let loginResponse = await Vue.prototype.$api.post(
        "/api/v1/login",
        {
          username: this.email,
          password: this.password,
          otp_code: this.otpCode,
        },
        Emit.None
      );
      this.setLocalState(
        loginResponse.data.name,
        loginResponse.data.user,
        loginResponse.data.token,
        loginResponse.data.time_added
      );

      setTimeout(() => {
        this.startChatAndNavigateUser(
          loginResponse.data.name,
          loginResponse.data.user
        );
      }, 100);
    } catch (error: any) {
      let errorMessage = error.response?.data.error.includes(
        "authentication code"
      )
        ? error.response?.data.error
        : "We couldn’t find an account matching the email and password you entered";
      this.showErrorSnackbar(errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * 🔨 Prefills the local state and checks whether all information about the user is correct. If so - navigates it to the dashboard
   */
  private async prefillLocalStateFromQuery(
    name: string,
    email: string,
    authToken: string,
    createdOn: number
  ) {
    this.isLoading = true;
    this.setLocalState(name, email, authToken, createdOn);
    this.$router.replace({ query: {} });
    try {
      await super.mounted();
      this.startChatAndNavigateUser(name, email);
    } catch (error) {
      this.showErrorSnackbar(
        "Something went wrong while redirecting. Please login with your email and password"
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * 🔨 Sets the local Vuex state
   */
  private async setLocalState(
    name: string,
    email: string,
    authToken: string,
    createdOn: number
  ) {
    Vue.axios.defaults.headers.common["Authorization"] = authToken;
    this.$store.commit("SET_USER_NAME", name);
    this.$store.commit("SET_USER_EMAIL", email);
    this.$store.commit("SET_AUTHORIZATION_TOKEN", authToken);
    this.$store.dispatch("changeUserCreationTime", createdOn);

    await this.$store.dispatch(
      "changeAppViewMode",
      this.$store.state.userPreferences.liteMode != undefined
        ? this.$store.state.userPreferences.liteMode
        : Number(createdOn) > 1649289600
    );
  }

  /**
   * 🏃‍♂️ Starts the Intercom messenger and redirects the user within the app
   */
  private startChatAndNavigateUser(name: string, email: string) {
    this.$store.dispatch("changeAuthorizationFlag", true);
    startMessenger({
      name: name,
      email: email,
    });
    this.$store.dispatch("fetchNotifications");
    this.$router.push("/my-portfolio");
  }
}
